// Dopasowanie klockow do wolnych mocowan.
//
// Zasada: klocki lacza sie WYLACZNIE przez sockety. Bliskosc klockow albo
// wspolna kratka nie tworza polaczenia. Dzieki temu konstrukcja budowana
// w edytorze zawsze pozostaje spojna - kazdy nowy klocek jest dokladany
// do istniejacego mocowania.
//
// Ten modul nie zna Plancka ani DOM.

import { getDefinition } from '../domain/catalog';
import { rotateVec, socketNormalWorld, socketWorldGrid } from '../domain/geometry';
import { validateBuilding } from '../domain/validate';
import type {
  Connection,
  PartDefinition,
  PartInstance,
  RobotBlueprint,
  Rotation,
  SocketKind,
  Vec2Like,
} from '../domain/types';

const ROTATIONS: readonly Rotation[] = [0, 90, 180, 270];
const TEMP_PART_ID = '__nowy__';

/** Wolne (nieuzyte) mocowanie w istniejacej konstrukcji. */
export interface SocketSlot {
  partId: string;
  socketId: string;
  kind: SocketKind;
  /** Pozycja w ukladzie siatki (jednostki klockowe). */
  grid: Vec2Like;
  normal: Vec2Like | null;
}

/** Propozycja postawienia klocka. */
export interface PlacementCandidate {
  /** Origin nowego klocka na siatce. */
  gridX: number;
  gridY: number;
  rotation: Rotation;
  /** Docelowe mocowanie w istniejacej konstrukcji. */
  slot: SocketSlot;
  /** Socket nowego klocka, ktory zostanie uzyty. */
  socketId: string;
  valid: boolean;
  /** Powod odrzucenia, gdy valid === false. */
  reason?: string;
}

/** Mocowania zajete przez istniejace polaczenia. */
function usedSockets(blueprint: RobotBlueprint): Set<string> {
  const used = new Set<string>();
  for (const conn of blueprint.connections) {
    used.add(`${conn.a.partId}#${conn.a.socketId}`);
    used.add(`${conn.b.partId}#${conn.b.socketId}`);
  }
  return used;
}

/** Wszystkie wolne mocowania w konstrukcji (kandydaci do doklejenia klocka). */
export function freeSockets(blueprint: RobotBlueprint): SocketSlot[] {
  const used = usedSockets(blueprint);
  const slots: SocketSlot[] = [];
  for (const instance of blueprint.parts) {
    const def = getDefinition(instance.typeId);
    for (const socket of def.sockets) {
      if (used.has(`${instance.id}#${socket.id}`)) continue;
      slots.push({
        partId: instance.id,
        socketId: socket.id,
        kind: socket.kind,
        grid: socketWorldGrid(instance, def, socket.id),
        normal: socketNormalWorld(def, socket.id, instance.rotation),
      });
    }
  }
  return slots;
}

/** Obrot, przy ktorym normalna nowego klocka jest przeciwna do mocowania. */
function rotationMatching(
  def: PartDefinition,
  socketId: string,
  slot: SocketSlot,
): Rotation | null {
  const socket = def.sockets.find((entry) => entry.id === socketId);
  if (!socket) return null;

  if (socket.kind === 'axle') {
    // Os kola nie ma kierunku - kazdy obrot pasuje.
    return slot.kind === 'axle' ? 0 : null;
  }
  if (slot.kind !== 'rigid' || !socket.normal || !slot.normal) return null;

  for (const rotation of ROTATIONS) {
    const normal = rotateVec(socket.normal, rotation);
    if (Math.abs(normal.x + slot.normal.x) < 1e-6 && Math.abs(normal.y + slot.normal.y) < 1e-6) {
      return rotation;
    }
  }
  return null;
}

/** Pozycja origin klocka, przy ktorej jego socket trafia dokladnie w slot. */
function originForSocket(
  def: PartDefinition,
  socketId: string,
  rotation: Rotation,
  slot: SocketSlot,
): Vec2Like {
  const socket = def.sockets.find((entry) => entry.id === socketId);
  if (!socket) throw new Error(`Socket "${socketId}" nie istnieje w czesci ${def.typeId}`);
  const rotated = rotateVec({ x: socket.x, y: socket.y }, rotation);
  return { x: slot.grid.x - rotated.x, y: slot.grid.y - rotated.y };
}

/**
 * Wspolne liczenie kandydatow dla jednego klocka: probujemy kazde wolne mocowanie
 * i kazdy jego socket, a wynik ocenia validateBuilding. `instanceId` to id,
 * pod ktorym klocek wystapi w konstrukcji probnej - dla montazu jest to id
 * zastepcze (klocek jeszcze nie istnieje), a dla przenoszenia - id istniejacego
 * klocka (pojawia sie w `base.parts` i zostaje podmieniony na nowa pozycje).
 */
function buildCandidates(
  base: RobotBlueprint,
  definition: PartDefinition,
  instanceId: string,
  slots: readonly SocketSlot[],
  options: { rotation?: Rotation; forceRotation?: boolean },
): PlacementCandidate[] {
  const candidates: PlacementCandidate[] = [];
  const otherParts = base.parts.filter((part) => part.id !== instanceId);

  for (const slot of slots) {
    for (const socket of definition.sockets) {
      const auto = rotationMatching(definition, socket.id, slot);
      if (auto === null) continue;

      const rotation = options.forceRotation ? (options.rotation ?? auto) : auto;
      if (socket.kind === 'rigid') {
        if (!slot.normal || !socket.normal) continue;
        const normal = rotateVec(socket.normal, rotation);
        if (
          Math.abs(normal.x + slot.normal.x) > 1e-6 ||
          Math.abs(normal.y + slot.normal.y) > 1e-6
        ) {
          continue;
        }
      }

      const origin = originForSocket(definition, socket.id, rotation, slot);
      const instance: PartInstance = {
        id: instanceId,
        typeId: definition.typeId,
        gridX: origin.x,
        gridY: origin.y,
        rotation,
      };
      const tentative: RobotBlueprint = {
        ...base,
        parts: [...otherParts, instance],
        connections: [
          ...base.connections,
          {
            a: { partId: instanceId, socketId: socket.id },
            b: { partId: slot.partId, socketId: slot.socketId },
          },
        ],
      };
      const issues = validateBuilding(tentative);

      candidates.push({
        gridX: origin.x,
        gridY: origin.y,
        rotation,
        slot,
        socketId: socket.id,
        valid: issues.length === 0,
        reason: issues[0]?.message,
      });
    }
  }

  return candidates;
}

/**
 * Kandydaci na postawienie NOWEGO klocka danego typu: dla kazdego wolnego
 * i zgodnego mocowania zwraca dokladna pozycje na siatce oraz to, czy montaz
 * sie uda.
 */
export function findPlacements(
  blueprint: RobotBlueprint,
  typeId: string,
  options: { rotation?: Rotation; forceRotation?: boolean } = {},
): PlacementCandidate[] {
  return buildCandidates(
    blueprint,
    getDefinition(typeId),
    TEMP_PART_ID,
    freeSockets(blueprint),
    options,
  );
}

/**
 * Wolne mocowania dostepne dla PRZENOSZONEGO klocka. Zdejmujemy jego wlasne
 * polaczenia (jego zajete sockety staja sie znow wolne), a nastepnie wykluczamy
 * jego wlasne sockety - klocek nie moze przyczepic sie sam do siebie.
 */
export function freeSocketsForMove(blueprint: RobotBlueprint, partId: string): SocketSlot[] {
  const base: RobotBlueprint = {
    ...blueprint,
    connections: blueprint.connections.filter(
      (conn) => conn.a.partId !== partId && conn.b.partId !== partId,
    ),
  };
  return freeSockets(base).filter((slot) => slot.partId !== partId);
}

/**
 * Kandydaci na PRZENIESIENIE istniejacego klocka. Bazuje na tych samych
 * mocowaniach co montaz, ale klocek zachowuje swoja pozycje w konstrukcji -
 * dzieki temu walidacja nadal pilnuje spojnosci reszty robota (klocek nie moze
 * zostac odczepiony ani rozspojnic korpusu).
 */
export function findMovePlacements(
  blueprint: RobotBlueprint,
  partId: string,
  options: { rotation?: Rotation; forceRotation?: boolean } = {},
): PlacementCandidate[] {
  const part = blueprint.parts.find((entry) => entry.id === partId);
  if (!part) return [];

  const base: RobotBlueprint = {
    ...blueprint,
    connections: blueprint.connections.filter(
      (conn) => conn.a.partId !== partId && conn.b.partId !== partId,
    ),
  };

  return buildCandidates(
    base,
    getDefinition(part.typeId),
    partId,
    freeSocketsForMove(blueprint, partId),
    options,
  );
}

/**
 * Polaczenie dla nowego klocka o podanym id. Kandydat nie zna jeszcze id czesci
 * (nadaje je edytor), wiec polaczenie budujemy jawnie - nie da sie przez pomylke
 * uzyc identyfikatora zastepczego.
 */
export function candidateConnection(candidate: PlacementCandidate, newPartId: string): Connection {
  return {
    a: { partId: newPartId, socketId: candidate.socketId },
    b: { partId: candidate.slot.partId, socketId: candidate.slot.socketId },
  };
}

/** Najblizszy kandydat do punktu na siatce (w jednostkach klockowych). */
export function nearestCandidate(
  candidates: readonly PlacementCandidate[],
  pointGrid: Vec2Like,
  maxDistanceGrid: number,
): PlacementCandidate | null {
  let best: PlacementCandidate | null = null;
  let bestDistance = maxDistanceGrid;
  for (const candidate of candidates) {
    const distance = Math.hypot(candidate.gridX - pointGrid.x, candidate.gridY - pointGrid.y);
    if (distance <= bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/** Srodek obrysu klocka na siatce - do podgladu i trafien. */
export function candidateCenterGrid(candidate: PlacementCandidate, def: PartDefinition): Vec2Like {
  const center = rotateVec({ x: def.footprint.w / 2, y: def.footprint.h / 2 }, candidate.rotation);
  return { x: candidate.gridX + center.x, y: candidate.gridY + center.y };
}

/**
 * Najblizszy kandydat do punktu na siatce, mierzony po SRODKU kandydata (z jego
 * wlasnym obrotem). Uzywane przy PRZENOSZENIU: pod palcem jest srodek klocka,
 * a kandydat moze miec inny obrot niz biezacy - porownywanie po originie biezacego
 * obrotu gubiloby trafienia (np. przelozenie modulu napedu na druga strone belki).
 */
export function nearestCandidateByCenter(
  candidates: readonly PlacementCandidate[],
  def: PartDefinition,
  pointGrid: Vec2Like,
  maxDistanceGrid: number,
): PlacementCandidate | null {
  let best: PlacementCandidate | null = null;
  let bestDistance = maxDistanceGrid;
  for (const candidate of candidates) {
    const center = candidateCenterGrid(candidate, def);
    const distance = Math.hypot(center.x - pointGrid.x, center.y - pointGrid.y);
    if (distance <= bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * Proba ponownego podlaczenia klocka (po obrocie albo wymianie).
 * Polaczenia sa geometryczne, wiec po zmianie obrotu trzeba je odtworzyc.
 * Zwraca null, gdy klocek zostalby calkiem odlaczony - takiej zmiany nie
 * przyjmujemy, zeby nigdy nie zostawic wiszacej czesci.
 */
export function reconnectPart(blueprint: RobotBlueprint, partId: string): RobotBlueprint | null {
  const part = blueprint.parts.find((entry) => entry.id === partId);
  if (!part) return null;

  const def = getDefinition(part.typeId);
  const taken = usedSockets(blueprint);
  const connections: Connection[] = [];

  for (const socket of def.sockets) {
    const ownKey = `${partId}#${socket.id}`;
    if (taken.has(ownKey)) continue;

    const grid = socketWorldGrid(part, def, socket.id);
    const normal = socketNormalWorld(def, socket.id, part.rotation);

    for (const slot of freeSockets(blueprint)) {
      if (slot.partId === partId) continue;
      if (taken.has(`${slot.partId}#${slot.socketId}`)) continue;
      if (slot.kind !== socket.kind) continue;
      if (Math.abs(slot.grid.x - grid.x) > 1e-6 || Math.abs(slot.grid.y - grid.y) > 1e-6) {
        continue;
      }
      if (socket.kind === 'rigid') {
        if (!normal || !slot.normal) continue;
        if (
          Math.abs(normal.x + slot.normal.x) > 1e-6 ||
          Math.abs(normal.y + slot.normal.y) > 1e-6
        ) {
          continue;
        }
      }

      connections.push({
        a: { partId, socketId: socket.id },
        b: { partId: slot.partId, socketId: slot.socketId },
      });
      taken.add(ownKey);
      taken.add(`${slot.partId}#${slot.socketId}`);
      break;
    }
  }

  if (connections.length === 0) return null;
  return { ...blueprint, connections: [...blueprint.connections, ...connections] };
}